import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import { UserProfile } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/user_profile_pb'
import { ClockIcon, GlobusIcon, PeopleIcon } from '@fingertip/icons'
import { addMinutes } from 'date-fns/addMinutes'
import { format, toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MediaImage } from '@/components/blocks/shared/media-image'
import { BookingLocationBadge } from '@/components/booking/booker/components/booking-location-badge'
import { BookerEventHeaderPolicy } from '@/components/booking/booker/header/booker-event-header-policy'
import { PriceBadge } from '@/components/booking/event-types/event-type-price-badge'
import { EventDurationBadge } from '@/components/booking/shared/event-duration-badge'
import { ColorCircles } from '@/components/shared/color-circles'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { formatTimezoneLabel } from '@/lib/dates/time-zone'
import { BookingSelection } from '@/lib/hooks/use-booking-selection'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useStoreConfigPricing } from '@/lib/hooks/use-store-config-pricing'
import { BookingStage } from '@/lib/stores/booker-store'
import { RootStoreContext } from '@/lib/stores/root-store'

type Props = {
  stage: BookingStage | null
  storeConfig?: StoreConfig
  bookingSelection?: BookingSelection
  amountTotalInCents?: number
  depositInCents?: number
  members?: UserProfile[]
}

const MAX_DESCRIPTION_LENGTH = 300

export const BookerEventHeader = observer(
  ({
    stage,
    storeConfig,
    bookingSelection,
    amountTotalInCents,
    depositInCents,
    members,
  }: Props) => {
    const { t } = useTranslation()
    const breakpoint = useBreakpoint()
    const [isExpanded, setIsExpanded] = useState(false)

    const {
      bookerStore: {
        selectedTimeslot,
        timeZone,
        selectedUserId,
        isCustomBooking,
        customLength,
        customPrice,
      },
    } = useContext(RootStoreContext)

    const finalLength =
      customLength || bookingSelection?.bookingItemsLength || 0

    const selectedMember: UserProfile | undefined = useMemo(() => {
      return members?.find((member) => member.id === selectedUserId)
    }, [members, selectedUserId])

    const mediaSize = useMemo(() => {
      if (breakpoint === 'xs' || breakpoint === 'xxs') {
        return 120
      }
      return 140
    }, [breakpoint])

    const { feeDetails } = useStoreConfigPricing(storeConfig)

    const truncateDescription = (description: string) => {
      if (description.length <= MAX_DESCRIPTION_LENGTH) return description
      return description.slice(0, MAX_DESCRIPTION_LENGTH) + '...'
    }

    const formattedStartDate = selectedTimeslot
      ? format(
          toZonedTime(selectedTimeslot, timeZone),
          `EEEE, MMM do, yyyy 'at' h:mm aaa`,
          { timeZone },
        )
      : null

    const formattedEndDate = selectedTimeslot
      ? format(
          toZonedTime(
            addMinutes(
              selectedTimeslot,
              (customLength
                ? customLength
                : bookingSelection?.bookingItemsLength) || 15,
            ),
            timeZone,
          ),
          'h:mm aaa',
          { timeZone },
        )
      : null

    if (!bookingSelection) return null

    return (
      <div className="flex flex-col space-y-4">
        {isCustomBooking && stage === 'DATE_PICKER' && (
          <div className="text-xl">{t('Select a date to get started')}</div>
        )}

        {/* Main content container */}
        <div className="flex flex-row justify-between gap-4">
          <div className="flex h-full flex-col items-start justify-between gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 text-base font-medium md:text-xl">
                <span>{bookingSelection.bookingItemsTitle}</span>
                <ColorCircles colors={bookingSelection.bookingItemsColors} />
              </div>

              {/* Description */}
              {bookingSelection?.bookingItemsDescription && (
                <div>
                  <p
                    className="text-xs md:text-sm"
                    dangerouslySetInnerHTML={{
                      __html: isExpanded
                        ? bookingSelection?.bookingItemsDescription.replace(
                            /\n/g,
                            '<br />',
                          )
                        : truncateDescription(
                            bookingSelection?.bookingItemsDescription,
                          ).replace(/\n/g, '<br />'),
                    }}
                  />
                  {bookingSelection?.bookingItemsDescription.length >
                    MAX_DESCRIPTION_LENGTH && (
                    <button
                      className="text-xs cursor-pointer text-muted-foreground hover:underline"
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      {isExpanded ? 'View less' : 'View more'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {!!bookingSelection.bookingItemsMedia && (
            <div>
              <MediaImage
                width={mediaSize}
                height={mediaSize}
                media={bookingSelection.bookingItemsMedia}
                title={bookingSelection.bookingItemsMedia.altText}
                className="overflow-hidden rounded-sm object-cover"
                style={{
                  width: mediaSize,
                  height: mediaSize,
                }}
                priority={true}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {/* Badges */}
          <div className="flex flex-row items-center gap-2">
            {!!bookingSelection && (
              <BookingLocationBadge
                location={bookingSelection.bookingItemsLocation}
              />
            )}

            {bookingSelection?.bookingItemsLength && (
              <EventDurationBadge
                duration={finalLength}
                isCustom={(customLength || 0) > 0}
              />
            )}

            {!!amountTotalInCents && (
              <PriceBadge
                storeConfig={storeConfig}
                priceInCents={amountTotalInCents}
                depositInCents={depositInCents}
                isCustom={(customPrice || 0) > 0}
              />
            )}
          </div>

          {/* Fee details */}
          {!!feeDetails && bookingSelection.bookingItemsPriceInCents > 0 && (
            <div className="flex flex-row items-center">
              <div className="flex flex-col text-xs">
                {feeDetails
                  .map((fee) => {
                    return `${fee.percent}% ${fee.label}`
                  })
                  .join(', ')}
              </div>
            </div>
          )}
        </div>

        {!!selectedUserId && !!selectedMember && stage !== 'MEMBER_PICKER' && (
          <div className="flex items-center text-sm">
            <PeopleIcon className="size-5 mr-2" />
            {selectedMember.name || selectedMember.email}
          </div>
        )}

        {!!selectedTimeslot &&
          stage !== 'AVAILABLE_SLOTS' &&
          stage !== 'DATE_PICKER' && (
            <div className="flex items-center text-sm">
              <ClockIcon className="size-5 mr-2" />
              {`${formattedStartDate} – ${formattedEndDate}`}
            </div>
          )}

        {!!timeZone &&
          stage !== 'AVAILABLE_SLOTS' &&
          stage !== 'DATE_PICKER' && (
            <div className="flex items-center text-sm">
              <GlobusIcon className="size-5 mr-2" />
              {formatTimezoneLabel(timeZone)}
            </div>
          )}

        {/* Booking policies container */}
        {!!storeConfig?.bookingPolicy && (
          <Accordion type="single" defaultValue={''} collapsible>
            <AccordionItem value="bookingPolicy">
              <AccordionTrigger className="text-sm" hideBackground>
                {t('view_booking_policy')}
              </AccordionTrigger>
              <AccordionContent className="p-0 text-sm text-muted-foreground">
                <BookerEventHeaderPolicy storeConfig={storeConfig} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    )
  },
)
